import React, { Fragment } from "react";
import { FormGroup, InputProps, Input, Label, InputGroup, InputGroupText } from "reactstrap";
import { FormValue } from "./contracts";

interface withValidationProps {
  formControl: FormValue;
}

const withValidation = <P extends object>(Component: React.ComponentType<P>) =>
  class WithValidation extends React.Component<P & withValidationProps> {
    render() {
      const props = { placeholder: this.props.formControl.placeholder, ...this.props };
      const showError = props.formControl.touched && !props.formControl.isValid;
      const inputClassName = showError ? "form-control form-control-danger" : "form-control";

      return (
        <FormGroup>
          <Component className={inputClassName} {...(props as P)} />
          {showError && props.formControl.validationError && (
            <span className="text-danger small">{props.formControl.validationError}</span>
          )}
        </FormGroup>
      );
    }
  };

type RadioButtonProps = InputProps & withValidationProps;
const _RadioButtons = function (props: RadioButtonProps) {
  const { formControl, name, onChange } = props;
  return (
    <div className="btn-group w-100" role="group">
      {(formControl.options || []).map((o, i) => {
        const isSelected = o.value === formControl.value;
        const inputId = `radio-${name}-${i}`;
        return (
          <Fragment key={i}>
            <input
              type="radio"
              className="btn-check"
              id={inputId}
              value={o.value}
              name={name}
              checked={isSelected}
              onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
              readOnly={!onChange}
            />
            <label
              className={`btn ${isSelected ? "btn-primary" : "btn-outline-secondary"}`}
              htmlFor={inputId}
            >
              {o.label}
            </label>
          </Fragment>
        );
      })}
    </div>
  );
};

type TextBox = InputProps & withValidationProps & { iconName?: string };
const _TextInput = function (props: TextBox) {
  const { iconName, formControl, ...elementProps } = props;
  const iconCss = `nc-icon ${iconName}`;
  const showError = formControl.touched && !formControl.isValid;
  const errorCss = showError ? "has-danger" : "";

  if (iconName) {
    return (
      <InputGroup className={errorCss}>
        <InputGroupText>
          <i className={iconCss} />
        </InputGroupText>
        <Input value={formControl.value} {...elementProps} />
      </InputGroup>
    );
  }
  return <Input value={formControl.value} {...elementProps} />;
};

type BoldLabelProps = { children: string };
const _RequiredLabel = function (props: BoldLabelProps) {
  return (
    <Label className="ds-label mb-1">
      {props.children}{" "}
      <span style={{ color: "var(--bs-secondary)" }}>*</span>
    </Label>
  );
};

export const RequiredLabel = _RequiredLabel;
export const TextInput = withValidation<TextBox>(_TextInput);
export const RadioButtons = withValidation<RadioButtonProps>(_RadioButtons);
